import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  shipments: [],
  activeShipments: [],
  completedShipments: [],
  userShipments: [],
  currentShipment: null,
  shipmentEvents: [],
  loading: {
    shipments: false,
    activeShipments: false,
    completedShipments: false,
    userShipments: false,
    currentShipment: false,
    events: false,
    creating: false,
    updating: false,
    deleting: false,
  },
  error: null,
  filters: {
    status: 'all', // all, active, completed
    search: '',
    dateRange: {
      start: null,
      end: null,
    },
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
};

export const shipmentsSlice = createSlice({
  name: 'shipments',
  initialState,
  reducers: {
    // Loading states
    setShipmentsLoading: (state, action) => {
      state.loading.shipments = action.payload;
    },
    setActiveShipmentsLoading: (state, action) => {
      state.loading.activeShipments = action.payload;
    },
    setCompletedShipmentsLoading: (state, action) => {
      state.loading.completedShipments = action.payload;
    },
    setUserShipmentsLoading: (state, action) => {
      state.loading.userShipments = action.payload;
    },
    setCurrentShipmentLoading: (state, action) => {
      state.loading.currentShipment = action.payload;
    },
    setEventsLoading: (state, action) => {
      state.loading.events = action.payload;
    },
    setCreatingLoading: (state, action) => {
      state.loading.creating = action.payload;
    },
    setUpdatingLoading: (state, action) => {
      state.loading.updating = action.payload;
    },
    setDeletingLoading: (state, action) => {
      state.loading.deleting = action.payload;
    },

    // Data setters
    setShipments: (state, action) => {
      state.shipments = action.payload;
      state.loading.shipments = false;
      state.error = null;
    },
    setActiveShipments: (state, action) => {
      state.activeShipments = action.payload;
      state.loading.activeShipments = false;
      state.error = null;
    },
    setCompletedShipments: (state, action) => {
      state.completedShipments = action.payload;
      state.loading.completedShipments = false;
      state.error = null;
    },
    setUserShipments: (state, action) => {
      state.userShipments = action.payload;
      state.loading.userShipments = false;
      state.error = null;
    },
    setCurrentShipment: (state, action) => {
      state.currentShipment = action.payload;
      state.loading.currentShipment = false;
      state.error = null;
    },
    setShipmentEvents: (state, action) => {
      state.shipmentEvents = action.payload;
      state.loading.events = false;
      state.error = null;
    },

    // CRUD operations
    addShipment: (state, action) => {
      state.shipments.unshift(action.payload);
      state.activeShipments.unshift(action.payload);
      state.loading.creating = false;
      state.error = null;
    },
    updateShipment: (state, action) => {
      const updatedShipment = action.payload;
      
      // Update in all arrays
      const updateInArray = (array) => {
        const index = array.findIndex(s => s.manifest === updatedShipment.manifest);
        if (index !== -1) {
          array[index] = updatedShipment;
        }
      };

      updateInArray(state.shipments);
      updateInArray(state.activeShipments);
      updateInArray(state.completedShipments);
      updateInArray(state.userShipments);

      // Update current shipment if it matches
      if (state.currentShipment?.manifest === updatedShipment.manifest) {
        state.currentShipment = updatedShipment;
      }

      state.loading.updating = false;
      state.error = null;
    },
    removeShipment: (state, action) => {
      const manifest = action.payload;
      
      state.shipments = state.shipments.filter(s => s.manifest !== manifest);
      state.activeShipments = state.activeShipments.filter(s => s.manifest !== manifest);
      state.completedShipments = state.completedShipments.filter(s => s.manifest !== manifest);
      state.userShipments = state.userShipments.filter(s => s.manifest !== manifest);
      
      if (state.currentShipment?.manifest === manifest) {
        state.currentShipment = null;
      }

      state.loading.deleting = false;
      state.error = null;
    },
    addShipmentEvent: (state, action) => {
      const newEvent = action.payload;
      state.shipmentEvents.unshift(newEvent);
      
      // Update current shipment events if it matches
      if (state.currentShipment?.manifest === newEvent.manifest) {
        state.currentShipment.events = state.currentShipment.events || [];
        state.currentShipment.events.unshift(newEvent);
      }
      
      state.error = null;
    },
    updateShipmentEvent: (state, action) => {
      const updatedEvent = action.payload;
      const index = state.shipmentEvents.findIndex(e => e.id === updatedEvent.id);
      
      if (index !== -1) {
        state.shipmentEvents[index] = updatedEvent;
      }
      
      // Update in current shipment if it matches
      if (state.currentShipment?.events) {
        const eventIndex = state.currentShipment.events.findIndex(e => e.id === updatedEvent.id);
        if (eventIndex !== -1) {
          state.currentShipment.events[eventIndex] = updatedEvent;
        }
      }
      
      state.error = null;
    },

    // Filters and pagination
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset to first page when filters change
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination = initialState.pagination;
    },

    // Error handling
    setError: (state, action) => {
      state.error = action.payload;
      // Reset all loading states on error
      Object.keys(state.loading).forEach(key => {
        state.loading[key] = false;
      });
    },
    clearError: (state) => {
      state.error = null;
    },

    // Clear data
    clearShipments: (state) => {
      state.shipments = [];
      state.activeShipments = [];
      state.completedShipments = [];
      state.userShipments = [];
      state.currentShipment = null;
      state.shipmentEvents = [];
    },
    clearCurrentShipment: (state) => {
      state.currentShipment = null;
      state.shipmentEvents = [];
    },
  },
});

export const {
  setShipmentsLoading,
  setActiveShipmentsLoading,
  setCompletedShipmentsLoading,
  setUserShipmentsLoading,
  setCurrentShipmentLoading,
  setEventsLoading,
  setCreatingLoading,
  setUpdatingLoading,
  setDeletingLoading,
  setShipments,
  setActiveShipments,
  setCompletedShipments,
  setUserShipments,
  setCurrentShipment,
  setShipmentEvents,
  addShipment,
  updateShipment,
  removeShipment,
  addShipmentEvent,
  updateShipmentEvent,
  setFilters,
  setPagination,
  resetFilters,
  setError,
  clearError,
  clearShipments,
  clearCurrentShipment,
} = shipmentsSlice.actions;

// Selectors
export const selectShipments = (state) => state.shipments;
export const selectAllShipments = (state) => state.shipments.shipments;
export const selectActiveShipments = (state) => state.shipments.activeShipments;
export const selectCompletedShipments = (state) => state.shipments.completedShipments;
export const selectUserShipments = (state) => state.shipments.userShipments;
export const selectCurrentShipment = (state) => state.shipments.currentShipment;
export const selectShipmentEvents = (state) => state.shipments.shipmentEvents;
export const selectShipmentsLoading = (state) => state.shipments.loading;
export const selectShipmentsError = (state) => state.shipments.error;
export const selectShipmentsFilters = (state) => state.shipments.filters;
export const selectShipmentsPagination = (state) => state.shipments.pagination;

// Filtered selectors
export const selectFilteredShipments = (state) => {
  const { shipments, activeShipments, completedShipments } = state.shipments;
  const { status, search, dateRange } = state.shipments.filters;
  
  let data = [];
  switch (status) {
    case 'active':
      data = activeShipments;
      break;
    case 'completed':
      data = completedShipments;
      break;
    default:
      data = shipments;
  }
  
  // Apply search filter
  if (search) {
    data = data.filter(shipment => 
      shipment.manifest.toLowerCase().includes(search.toLowerCase()) ||
      shipment.reference.toLowerCase().includes(search.toLowerCase()) ||
      shipment.origin?.origin_name.toLowerCase().includes(search.toLowerCase()) ||
      shipment.destination?.destination_name.toLowerCase().includes(search.toLowerCase())
    );
  }
  
  // Apply date range filter
  if (dateRange.start && dateRange.end) {
    data = data.filter(shipment => {
      const shipmentDate = new Date(shipment.created_at);
      return shipmentDate >= new Date(dateRange.start) && shipmentDate <= new Date(dateRange.end);
    });
  }
  
  return data;
};

export default shipmentsSlice.reducer;